import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { yearMonthBadge } from "@/lib/periodBadge";
import { Layout } from "@/components/layout/Layout";
import {
  getNotifications,
  markNotificationRead,
  markNotificationUnread,
  markAllNotificationsRead,
  deleteNotification,
  clearReadNotifications,
  notificationTargetUrl,
  type AppNotification,
  type NotificationType,
} from "@/lib/api";
import { format } from "date-fns";
import {
  Bell, CheckCheck, Trash2, Check, AtSign, ClipboardCheck,
  GitBranch, Users, CheckCircle, MessageSquare, MessageCircle,
  Upload, CheckSquare, Search, Settings, X, ExternalLink,
  CheckCircle2, FileText, Unlock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLocation } from "wouter";
import { useState, useMemo } from "react";

// ─── Period label helper ──────────────────────────────────────────────────────

function periodBadge(n: AppNotification): string | null {
  return yearMonthBadge(n.tax_year, n.period_label);
}

// ─── Category tab definitions ────────────────────────────────────────────────

type TabId = "all" | "projects" | "mentions" | "client";

const TABS: { id: TabId; label: string }[] = [
  { id: "all",      label: "All" },
  { id: "projects", label: "Projects" },
  { id: "mentions", label: "Mentions" },
  { id: "client",   label: "Client Activity" },
];

const TAB_TYPES: Record<TabId, NotificationType[] | null> = {
  all: null,
  projects: ["task_assigned","task_unblocked","task_comment","stage_changed","status_changed","project_assigned","project_completed","project_comment","project_ready_for_review"],
  mentions: ["mention","mention_on_note"],
  client:   ["client_message","client_document_upload","client_task_complete","client_document_deleted","client_chat_message","client_task_comment","document_delete_requested","document_deleted","document_delete_decided"],
};

// ─── Icon per type ────────────────────────────────────────────────────────────

function notifIcon(type: AppNotification["type"]) {
  if (type === "task_assigned")         return <ClipboardCheck className="w-4 h-4 text-blue-500 shrink-0" />;
  if (type === "task_unblocked")        return <Unlock         className="w-4 h-4 text-green-500 shrink-0" />;
  if (type === "feedback_resolved")     return <CheckCircle2   className="w-4 h-4 text-emerald-500 shrink-0" />;
  if (type === "document_delete_requested" || type === "document_deleted" || type === "document_delete_decided")
                                        return <Trash2         className="w-4 h-4 text-amber-600 shrink-0" />;
  if (type === "task_comment")          return <MessageSquare  className="w-4 h-4 text-blue-400 shrink-0" />;
  if (type === "mention")               return <AtSign         className="w-4 h-4 text-violet-500 shrink-0" />;
  if (type === "stage_changed" || type === "status_changed")
                                        return <GitBranch      className="w-4 h-4 text-amber-500 shrink-0" />;
  if (type === "project_assigned")      return <Users          className="w-4 h-4 text-green-500 shrink-0" />;
  if (type === "project_completed")     return <CheckCircle    className="w-4 h-4 text-emerald-500 shrink-0" />;
  if (type === "project_comment")       return <MessageSquare  className="w-4 h-4 text-sky-500 shrink-0" />;
  if (type === "client_message")        return <MessageCircle  className="w-4 h-4 text-teal-500 shrink-0" />;
  if (type === "client_document_upload") return <Upload        className="w-4 h-4 text-orange-500 shrink-0" />;
  if (type === "client_task_complete")  return <CheckSquare    className="w-4 h-4 text-emerald-400 shrink-0" />;
  if (type === "project_ready_for_review") return <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />;
  if (type === "client_document_deleted")  return <Trash2       className="w-4 h-4 text-destructive shrink-0" />;
  if (type === "mention_on_note")          return <FileText     className="w-4 h-4 text-violet-500 shrink-0" />;
  if (type === "client_chat_message")      return <MessageCircle className="w-4 h-4 text-blue-500 shrink-0" />;
  if (type === "client_task_comment")      return <MessageSquare className="w-4 h-4 text-teal-500 shrink-0" />;
  return <Bell className="w-4 h-4 text-muted-foreground shrink-0" />;
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Notifications() {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const [tab, setTab]       = useState<TabId>("all");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "unread" | "read">("all");

  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: getNotifications,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const all = data?.notifications ?? [];
  const unreadCount = data?.unread_count ?? 0;

  // ── mutations ──────────────────────────────────────────────────────────────
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["notifications"] });
  const markReadMut    = useMutation({ mutationFn: markNotificationRead,    onSuccess: invalidate });
  const markUnreadMut  = useMutation({ mutationFn: markNotificationUnread,  onSuccess: invalidate });
  const markAllMut     = useMutation({ mutationFn: markAllNotificationsRead, onSuccess: invalidate });
  const deleteMut      = useMutation({ mutationFn: deleteNotification,       onSuccess: invalidate });
  const clearReadMut   = useMutation({ mutationFn: clearReadNotifications,   onSuccess: invalidate });

  // ── filtering ──────────────────────────────────────────────────────────────
  const visible = useMemo(() => {
    let rows = all;
    const types = TAB_TYPES[tab];
    if (types) rows = rows.filter((n) => types.includes(n.type));
    if (filter === "unread") rows = rows.filter((n) => !n.is_read);
    if (filter === "read")   rows = rows.filter((n) =>  n.is_read);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter(
      (n) =>
        (n.title ?? "").toLowerCase().includes(q) ||
        (n.body?.toLowerCase().includes(q) ?? false) ||
        (n.account_name?.toLowerCase().includes(q) ?? false)
    );
    return rows;
  }, [all, tab, filter, search]);

  const readCount = all.filter((n) => n.is_read).length;

  // ── navigate + mark read ───────────────────────────────────────────────────
  const viewClient = (n: AppNotification) => {
    if (!n.is_read) markReadMut.mutate(n.id);
    const url = notificationTargetUrl(n);
    if (url && url !== "/projects") navigate(url);
  };

  const viewProject = (n: AppNotification) => {
    if (!n.is_read) markReadMut.mutate(n.id);
    const url = notificationTargetUrl(n);
    if (url) navigate(url);
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-4">

        {/* ── Header ── */}
        <div className="flex flex-col items-center gap-3 text-center">
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center justify-center gap-2">
              <Bell className="w-6 h-6" />
              Notifications
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {unreadCount > 0 ? `${unreadCount} Unread` : "All Caught Up"}
            </p>
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {readCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => clearReadMut.mutate()}
                disabled={clearReadMut.isPending}
                title={`Permanently delete all ${readCount} read notification${readCount === 1 ? "" : "s"}`}
                className="gap-1.5 text-muted-foreground hover:text-destructive hover:border-destructive/50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete All Read ({readCount})
              </Button>
            )}
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={() => markAllMut.mutate()} disabled={markAllMut.isPending} className="gap-1.5">
                <CheckCheck className="w-3.5 h-3.5" />
                Mark All Read
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate("/settings#notification-prefs")} className="gap-1.5">
              <Settings className="w-3.5 h-3.5" />
              Preferences
            </Button>
          </div>
        </div>

        {/* ── Category tabs ── */}
        <div className="flex gap-0 border-b justify-center overflow-x-auto">
          {TABS.map((t) => {
            const types = TAB_TYPES[t.id];
            const count = types
              ? types.reduce((s, type) => s + all.filter((n) => n.type === type && !n.is_read).length, 0)
              : all.filter((n) => !n.is_read).length;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                  tab === t.id
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
                {count > 0 && (
                  <span className="text-[10px] font-bold bg-primary text-primary-foreground rounded-full min-w-[16px] h-4 px-0.5 flex items-center justify-center">
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Search + read filter ── */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by client, title, or message…"
              className="pl-8 h-8 text-sm"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex rounded-md border overflow-hidden text-sm">
            {(["all", "unread", "read"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 capitalize transition-colors ${
                  filter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* ── List ── */}
        {isLoading ? (
          <div className="text-sm text-muted-foreground py-16 text-center">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="text-sm text-muted-foreground py-16 text-center">
            <Bell className="w-8 h-8 mx-auto mb-3 opacity-20" />
            {search || filter !== "all" ? "No notifications match your filters." : "No notifications yet."}
          </div>
        ) : (
          <div className="rounded-lg border divide-y bg-card overflow-hidden">
            {visible.map((n) => (
              <NotifRow
                key={n.id}
                n={n}
                onMarkRead={() => markReadMut.mutate(n.id)}
                onMarkUnread={() => markUnreadMut.mutate(n.id)}
                onDelete={() => deleteMut.mutate(n.id)}
                onViewClient={() => viewClient(n)}
                onViewProject={() => viewProject(n)}
                isDeleting={deleteMut.isPending && deleteMut.variables === n.id}
              />
            ))}
          </div>
        )}

        {visible.length > 0 && (
          <p className="text-xs text-muted-foreground text-center">
            Showing {visible.length}{visible.length < all.length ? ` of ${all.length}` : ""} notification{visible.length !== 1 ? "s" : ""}
          </p>
        )}
      </div>
    </Layout>
  );
}

// ─── Row component ────────────────────────────────────────────────────────────

function NotifRow({
  n,
  onMarkRead,
  onMarkUnread,
  onDelete,
  onViewClient,
  onViewProject,
  isDeleting,
}: {
  n: AppNotification;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onDelete: () => void;
  onViewClient: () => void;
  onViewProject: () => void;
  isDeleting: boolean;
}) {
  const period = periodBadge(n);
  const hasNav = !!(n.account_id || n.related_project_id);

  return (
    <div
      onClick={n.is_read ? onMarkUnread : onMarkRead}
      title={n.is_read ? "Mark as unread" : "Mark as read"}
      className={`flex items-start gap-3 px-4 py-3.5 hover:bg-muted transition-colors cursor-pointer ${
        !n.is_read ? "bg-primary/5 border-l-4 border-l-primary" : "border-l-4 border-l-transparent"
      } ${isDeleting ? "opacity-40 pointer-events-none" : ""}`}
    >
      {/* ── Green read/unread checkbox (LEFT) ── */}
      <button
        onClick={(e) => { e.stopPropagation(); (n.is_read ? onMarkUnread : onMarkRead)(); }}
        title={n.is_read ? "Mark as unread" : "Mark as read"}
        className={`shrink-0 mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
          n.is_read
            ? "bg-green-500 border-green-500 hover:bg-green-400 hover:border-green-400"
            : "border-muted-foreground/40 hover:border-green-400 bg-transparent"
        }`}
      >
        {n.is_read && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
      </button>

      {/* ── Type icon ── */}
      <div className="mt-0.5 shrink-0">{notifIcon(n.type)}</div>

      {/* ── Content ── */}
      <div className="flex-1 min-w-0">

        {/* Client pill + period badge */}
        {(n.account_name || period) && (
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            {n.account_name && (
              <button
                onClick={(e) => { e.stopPropagation(); onViewClient(); }}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary text-primary-foreground leading-none hover:opacity-80 transition-opacity"
              >
                {n.account_name}
              </button>
            )}
            {period && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-100 text-sky-800 border border-sky-200 leading-none">
                {period}
              </span>
            )}
          </div>
        )}

        {/* Title */}
        <p className={`text-sm leading-snug ${!n.is_read ? "font-semibold" : "text-foreground/80"}`}>
          {n.title}
        </p>

        {/* Body */}
        {n.body && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
        )}

        {/* Navigation links */}
        {hasNav && (
          <div className="flex items-center gap-3 mt-1.5">
            {n.account_id && (
              <button
                onClick={(e) => { e.stopPropagation(); onViewClient(); }}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                View client
              </button>
            )}
            {n.related_project_id && (
              <button
                onClick={(e) => { e.stopPropagation(); onViewProject(); }}
                className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                View project
              </button>
            )}
          </div>
        )}

        {/* Timestamp */}
        <p className="text-xs text-muted-foreground/50 mt-1">
          {format(new Date(n.created_at), "MMM d, yyyy 'at' h:mm a")}
        </p>
      </div>

      {/* ── Delete ── */}
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        title="Delete permanently"
        className="shrink-0 mt-0.5 p-1.5 rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
